
import { Recipe } from "@/types/recipe";
import { supabase } from "@/integrations/supabase/client";
import { authService } from "@/services/auth";
import { formatRecipeForDb, formatRecipeUpdatesForDb, mapDbRecipeToRecipe } from "./recipeUtils";
import { withErrorHandling } from "../utils/errorHandling";
import { DbRecipe } from "@/types/database";
import { RecipeImageService } from "@/services/recipeImageService";

/**
 * Service for creating, updating and deleting recipes
 */
export const recipeMutationService = {
  /**
   * Save a new recipe
   */
  saveRecipe: withErrorHandling(
    async (recipe: Recipe, suggestionId?: string): Promise<Recipe | null> => {
      // Get current user using authService directly
      const currentUser = await authService.getCurrentUser();
      
      if (!currentUser) {
        throw new Error("No authenticated user found");
      }
      
      const userId = currentUser.userId;
      
      // Format recipe for database insertion
      const recipeWithSuggestionId = { ...recipe, suggestion_id: suggestionId || recipe.suggestion_id };
      const formattedRecipe = formatRecipeForDb(recipeWithSuggestionId, userId);
      
      const { data, error: saveError } = await supabase
        .from("recipes")
        .insert(formattedRecipe)
        .select()
        .single();

      if (saveError) {
        throw saveError;
      }

      // Convert from database format to Recipe type
      const savedRecipe = mapDbRecipeToRecipe(data as DbRecipe);

      // Process image in background if recipe has an image URL
      if (savedRecipe && recipe.image_url && RecipeImageService.isValidImageUrl(recipe.image_url)) {
        console.log(`🖼️ Triggering background image processing for recipe: ${savedRecipe.id}`);
        
        // Process image asynchronously - don't await to avoid blocking recipe creation
        RecipeImageService.processRecipeImage(savedRecipe.id, recipe.image_url)
          .then(async (result) => {
            if (result.success && result.storage_image_url) {
              console.log(`✅ Image processing successful for recipe ${savedRecipe.id}, updating storage URL`);
              
              // Update the recipe with the processed image URL
              await supabase
                .from("recipes")
                .update({
                  storage_image_url: result.storage_image_url,
                  storage_image_path: result.storage_image_path || null
                })
                .eq("id", savedRecipe.id);
            } else {
              console.warn(`⚠️ Image processing failed for recipe ${savedRecipe.id}:`, result.error);
            }
          })
          .catch((error) => {
            console.error(`❌ Image processing error for recipe ${savedRecipe.id}:`, error);
          });
      }

      return savedRecipe;
    },
    "Error saving recipe"
  ),

  /**
   * Update an existing recipe
   */
  updateRecipe: withErrorHandling(
    async (id: string, updates: Partial<Recipe>): Promise<Recipe | null> => {
      // Get current user using authService directly
      const currentUser = await authService.getCurrentUser();
      
      if (!currentUser) {
        throw new Error("No authenticated user found");
      }
      
      // Format updates for database
      const formattedUpdates = formatRecipeUpdatesForDb(updates);
      
      const { data, error: updateError } = await supabase
        .from("recipes")
        .update(formattedUpdates)
        .eq("id", id)
        .select()
        .single();

      if (updateError) {
        throw updateError;
      }

      // Convert from database format to Recipe type
      return mapDbRecipeToRecipe(data as DbRecipe);
    },
    "Error updating recipe"
  ),

  /**
   * Delete a recipe by ID
   */
  deleteRecipe: withErrorHandling(
    async (id: string): Promise<boolean> => {
      const { error } = await supabase.from("recipes").delete().eq("id", id);

      if (error) {
        throw error;
      }

      return true;
    },
    "Error deleting recipe"
  )
};
